import { logger } from '../utils/logger';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated, object = authenticated
  const [loading, setLoading] = useState(true);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(data);
    } catch (error) {
      // Try refresh token
      try {
        await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Auto-refresh access token every 3.5 hours (token expires in 4h)
  useEffect(() => {
    if (!user || user === false) return;
    const interval = setInterval(async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
      } catch (err) {
        logger.warn('Token refresh failed:', err?.message);
      }
    }, 3.5 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      if (data.requires_2fa) {
        return { success: false, requires_2fa: true, two_factor_method: data.two_factor_method, two_factor_token: data.two_factor_token, message: data.message };
      }
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  }, []);

  const verify2fa = useCallback(async (twoFactorToken, code) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/2fa/verify-login`,
        { two_factor_token: twoFactorToken, code },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message
      };
    }
  }, []);

  const register = useCallback(async (email, password, name, license_number, license_issuer, referral_code) => {
    try {
      const payload = { email, password, name, license_number, license_issuer };
      if (referral_code) payload.referral_code = referral_code;
      const { data } = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        payload,
        { withCredentials: true }
      );
      setUser(data);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logger.error('Logout error:', error);
    } finally {
      setUser(false);
    }
  }, []);

  const contextValue = useMemo(() => ({ user, loading, login, verify2fa, register, logout, checkAuth }), [user, loading, login, verify2fa, register, logout, checkAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
